import axios from "axios";
import { ApiError } from "../../Models/ApiError";
import { HandleError } from "../../Helpers/ErrorHandler";
import { OrderList } from "../../Models/OrderList";
import { Order } from "../../Models/Order";
import { ValidCountLocation } from "../../Models/ValidCountLocation";
import { StockRequestCreateRequest } from "../../Models/StockRequestCreateRequest";
import { StockRequestSaveRequest } from "../../Models/StockRequestSaveRequest";
import { Product } from "../../Models/Product";
import { ScanItemResponse } from "../../Models/ScanItemResponse";

const API_URL = process.env.REACT_APP_API_URL;

export const getStockRequestsAPI = async (
  serverUrl: string,
  dbKey: string,
  showOnlyUnlocked: boolean = false,
  pickStatus: string = "",
  filterDays: number = 0,
  resultLimit: number = 0,
): Promise<OrderList> => {
  return new Promise<OrderList>((resolve, reject) => {
    let url = `${API_URL}stockrequest?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`;
    if (showOnlyUnlocked) {
      url += "&showOnlyUnlocked=true";
    }
    if (pickStatus) {
      url += `&pickStatus=${encodeURI(pickStatus)}`;
    }
    if (filterDays > 0) {
      url += `&filterDays=${filterDays}`;
    }
    if (resultLimit > 0) {
      url += `&resultLimit=${resultLimit}`;
    }
    axios
      .get<OrderList>(url)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const getStockRequestLocationsAPI = async (serverUrl: string, dbKey: string): Promise<ValidCountLocation[]> => {
  return new Promise<ValidCountLocation[]>((resolve, reject) => {
    axios
      .get<ValidCountLocation[]>(`${API_URL}stockrequest/locations?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

export const getStockRequestAPI = async (transRefCode: string, serverUrl: string, dbKey: string): Promise<Order> => {
  return new Promise<Order>((resolve, reject) => {
    axios
      .get<Order>(`${API_URL}stockrequest/${encodeURIComponent(transRefCode)}?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

//a scanned label as a pallet serial in the From location - status 2 means it is not one, try it as a barcode
export const scanStockRequestSerialAPI = async (barcode: string, locationNo: number | undefined, serverUrl: string, dbKey: string): Promise<ScanItemResponse> => {
  return new Promise<ScanItemResponse>((resolve, reject) => {
    let url = `${API_URL}stockrequest/scanserial?barcode=${encodeURIComponent(barcode)}&serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`;
    if (locationNo) {
      url += `&locationNo=${locationNo}`;
    }
    axios
      .get<ScanItemResponse>(url)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

//stock details for a new line on a draft request
export const getStockRequestItemAPI = async (stockCode: string, serverUrl: string, dbKey: string): Promise<Product> => {
  return new Promise<Product>((resolve, reject) => {
    axios
      //stock codes can contain '/', so the code goes in the query string rather than the path
      .get<Product>(`${API_URL}stockrequest/item?stockCode=${encodeURIComponent(stockCode)}&serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

//posts a draft request - header, lines and picked serials - to eSuite and returns it as created
export const createStockRequestAPI = async (model: StockRequestCreateRequest, serverUrl: string, dbKey: string): Promise<Order> => {
  return new Promise<Order>((resolve, reject) => {
    axios
      .post<Order>(`${API_URL}stockrequest?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`, model)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};

//saves the changes made to an existing request - line changes, removed lines and picks - and returns it as it now stands
export const saveStockRequestAPI = async (model: StockRequestSaveRequest, serverUrl: string, dbKey: string): Promise<Order> => {
  return new Promise<Order>((resolve, reject) => {
    axios
      .post<Order>(`${API_URL}stockrequest/save?serverUrl=${encodeURI(serverUrl)}&dbKey=${dbKey}`, model)
      .then((resp) => {
        return resolve(resp.data);
      })
      .catch((error: any) => {
        const apiErr: ApiError = HandleError(error);
        return reject(apiErr);
      });
  });
};
